import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import { getUserProfile } from "../configuration/login/loginSlice";

import { fetchMaintenance } from "../api/fetchMaintenance";
import { fetchUptimeAssets } from "../api/fetchAssets";
import { fetchCheckupByMetricAndModel } from "../api/fetchCheckup";
import { fetchDealershipsByGeolocation, fetchDealerships, fetchDealershipsByStateAndCity, fetchFavoritesDealerships } from "../api/fetchDealerships";
import { fetchConsultants } from "../api/fetchConsultants";
import { fetchScheduling, fetchSchedulingByChassisList } from "../api/fetchScheduling";
import { fetchDrivers } from "../api/fetchDrivers";

import { Asset, AssetGeoLocation } from "../models/asset";
import { Checkup, CheckupAdicionalService } from "../models/checkup";
import { Driver } from "../models/driver";
import { Dealership, DealershipConsultant } from "../models/dealership";


import useAssetGeoLocation from "./useAssetGeoLocation";
import useAssets from "./useAssets";

import { getAddressByGeoLocation, getGeoLocationByAddress } from "../services/assetService";
import { addDays, getFirstDayOfWeek, getLastDayOfWeek, getStringWeekDaysStartingFromDate } from "../utils/dateUtils";
import { createAppointmentFormFromScheduling } from "../services/schedulingService";
import { states } from "../utils/locationUtils";

export interface IAppointmentForm {
    assetsAndDrivers?: IAssetAndDriverForm;
    dealership?: Dealership;
    schedulingTypes?: string[];
    dateAndConsultant?: IDateAndConsultantForm;
    scheduleNumber?: string;
}
export function useSchedulingForm() {
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState(1);
    const [appointmentForm, setAppointmentForm] = useState<IAppointmentForm>({
        assetsAndDrivers: {
            driver: { id: "", name: "-", license: "", phone: "", email: "" }
        },
        dealership: { id: "", name: "", address: "", geoLocation: { lat: 0, lng: 0 } },
        dateAndConsultant: {},
    });
    const [appointmentId, setAppointmentId] = useState<string | null>(null);
    const {allAssets} = useAssets();
    const [assets, setAssets] = useState<Asset[]>([]);
    const [drivers, setDrivers] = useState<Driver[]>([]);

    const [searchParams] = useSearchParams();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            const drivers = await fetchDrivers();
            setAssets(allAssets);
            setDrivers(drivers);

            const schedulingId = searchParams.get("scheduling-id");
            if (schedulingId) {
                const appointment = await fetchScheduling(schedulingId);
                if (appointment) {
                    setAppointmentForm(createAppointmentFormFromScheduling(appointment, []));
                    setStep(3);
                    setAppointmentId(schedulingId);
                }
            }

            setLoading(false);
        }

        fetchData()
    }, []);

    return {
        loading,
        step, setStep,
        assets, drivers,
        appointmentForm, setAppointmentForm,
        appointmentId,
    }
}

export interface IAssetAndDriverForm {
    asset?: Asset;
    scheduleByDriver?: boolean;
    checkup?: Checkup;
    driver?: Driver;
    schedulingTypes?: string[];
    aditionalServices?: CheckupAdicionalService[];
    comments?: string | null;
    obs?: string;
}
export function useSchedulingFormAsset(setForm: (form: IAssetAndDriverForm) => void, form?: IAssetAndDriverForm) {

    const [loading, setLoading] = useState(false);
    const [vehicleLoading, setVehicleLoading] = useState(false);
    const [vehicleError, setVehicleError] = useState<string>('');
    const [searchAssetTerm, setSearchAssetTerm] = useState("");
    const [disableAssetGroup, setDisableAssetGroup] = useState(false);
    const [assetId, setAssetId] = useState("");
    const [showComments, setShowComments] = useState(false);
    const [hasProcessedUrl, setHasProcessedUrl] = useState(false);

    const { getAssetGeoLocation } = useAssetGeoLocation()

    const { allAssets } = useAssets();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const checkup = await getCheckupByMetric(
                form?.asset?.odometer ?? 0,
                form?.asset?.hourmeter ?? 0,
                form?.asset?.chassis ?? "",
                form?.asset?.group ?? "",
                form?.asset?.modelCode ?? ""
            );
            setForm({ ...form, checkup });
            setLoading(false);
        }
        fetchData();
    }, [form?.asset?.modelCode]);

    const filteredAssets = (assets: Asset[]): Asset[] => assets
        .filter((asset) => {
            const isChassisValid = asset.chassis !== '-';
            const searchTerm = searchAssetTerm.toLowerCase();
            const isNameValid = asset.name?.toLowerCase().includes(searchTerm);
            const isChassisMatch = asset.chassis?.toLowerCase().includes(searchTerm);
            const isPlateMatch = asset.licensePlate && asset.licensePlate.toLowerCase().includes(searchTerm);
            
            return isChassisValid && (isNameValid || isChassisMatch || isPlateMatch);
        }).slice(0, 20)

    useEffect(() => {
        const fetchData = async () => {
            const queryParams = new URLSearchParams(window.location.search);
            const assetId = queryParams.get("asset");
            
            const summaryAsset = allAssets.find(asset => asset.id === assetId);
            
            const licensePlate = summaryAsset?.licensePlate ?? '';
            const identification = queryParams.get("chassis") ?? summaryAsset?.chassis ?? '';
            const name = queryParams.get("name") ?? summaryAsset?.name ?? '';
            
            console.log('URL params (useSchedulingFormAsset):', { assetId, identification, name, licensePlate });
            console.log('form?.asset:', form?.asset);
            
            if (assetId && identification && !hasProcessedUrl) {
                console.log('Executando chooseAsset (useSchedulingFormAsset)...');
                setHasProcessedUrl(true);
                await chooseAsset(assetId, identification, name, licensePlate);
                setAssetId(assetId);
            } else {
                console.log('Condição não atendida (useSchedulingFormAsset):', { assetId, identification, hasProcessedUrl });
            }
        };
        
        if (allAssets.length > 0) {
          fetchData();
        }
    }, [allAssets]);

    const chooseAsset = async (assetId: string, identification: string, name: string, licensePlate: string) => {
        try {
            console.log('chooseAsset (useSchedulingFormAsset) chamada com:', { assetId, identification, name, licensePlate });
            setVehicleLoading(true);
            setVehicleError('');
            
            const [assetsFetched] = await fetchMaintenance([{id: assetId, identification, name}]);
            console.log('assetsFetched (useSchedulingFormAsset):', assetsFetched);
            
            if (!assetsFetched.length) {
                setVehicleError('Veículo não encontrado');
                return;
            }

            const asset = assetsFetched[0];

            // Verificar se o veículo já tem agendamento
            console.log('Verificando agendamentos para chassi (useSchedulingFormAsset):', asset.chassis);
            const existingSchedules = await fetchSchedulingByChassisList([asset.chassis]);
            console.log('existingSchedules (useSchedulingFormAsset):', existingSchedules);
            
            if (existingSchedules && existingSchedules.length > 0) {
                console.log('Agendamento existente encontrado (useSchedulingFormAsset)!');
                setVehicleError('Erro ao agendar. Para esse veiculo já existe um agendamento.');
                // Não retornar aqui, deixar carregar as informações do veículo
            }

            if (form) {
                const fromEP = !!((licensePlate && licensePlate !== '' && licensePlate !== '-') || (asset.licensePlate && asset.licensePlate !== '-'));

                asset.licensePlateFromEP = fromEP;

                form.asset = asset;
                form.asset.chassis = identification;
                form.asset.name = name;
                if (licensePlate && licensePlate !== '' && licensePlate !== '-') {
                    form.asset.licensePlate = licensePlate;
                } else if (asset.licensePlate && asset.licensePlate !== '-') {
                    form.asset.licensePlate = asset.licensePlate;
                }

                form.asset.licensePlateFromEP = !!fromEP;
            }

            const geoLocation = await getAssetGeoLocation(assetId);
            asset.geoLocation = geoLocation;

            if (!asset.odometer || !asset.hourmeter) {
                const uptimeAssets = await fetchUptimeAssets([asset.chassis]);
                const uptimeAsset = uptimeAssets[0];
                if (!asset.odometer && uptimeAsset?.odometer) {
                    asset.odometer = uptimeAsset?.odometer;
                    asset.lastOdometerUpdate = uptimeAsset?.lastOdometerUpdate;
                }

                if (!asset.hourmeter && uptimeAsset?.hourmeter) {
                    asset.hourmeter = uptimeAsset?.hourmeter;
                    asset.lastHourmeterUpdate = uptimeAsset?.lastHourmeterUpdate;
                }
            }

            console.log('Asset carregado com sucesso (useSchedulingFormAsset):', asset);
            
            chooseCheckup(asset);
            setDisableAssetGroup(!!asset.group);
            console.log('Form atualizado com sucesso (useSchedulingFormAsset)');
        } catch (error: any) {
            console.error('Erro ao buscar informações do veículo:', error);
            setVehicleError('Erro ao carregar informações do veículo. Tente novamente.');
        } finally {
            setVehicleLoading(false);
        }
    };

    const chooseCheckup = async (asset: Asset | undefined) => {
        const checkup = asset
            ? await fetchCheckupByMetricAndModel(
                asset?.odometer ?? "",
                asset?.hourmeter ?? "",
                asset?.chassis ?? "",
                asset?.group ?? "",
                asset?.modelCode ?? ""
            )
            : undefined;
        setForm({
            ...form,
            checkup,
            asset,
        });
    };

    const getCheckupByMetric = async (
        odometer: number,
        hourmeter: number,
        chassis: string,
        group: string,
        model?: string
    ) => {
        return await fetchCheckupByMetricAndModel(
            odometer,
            hourmeter,
            chassis,
            group,
            model
        );
    };

    const formIsValid = (): boolean => {
        return !!(
            form?.asset &&
            form?.asset.hourmeter &&
            form?.asset.odometer &&
            form?.checkup &&
            form?.schedulingTypes &&
            form?.schedulingTypes.length > 0
        );
    };

    const aditionalServices = [
        { id: "fluid-analysis", name: "Análise de fluído (Predic)", price: 50 },
        { id: "filter-cleaning", name: "Limpeza de filtro (Limpar Auto)", price: 10 },
        { id: "break-cleaning", name: "Limpeza de freios (Limpar Auto)", price: 60 },
        { id: "oxi-sanitization", name: "Oxisanitização (Motran)", price: 10 },
    ];

    const setCheckup = useMemo(
        () => debounce(async (metricValue: number, metricType: string, asset?: Asset) => {
            setLoading(true);
            const checkup = await getCheckupByMetric(
                metricType === 'odometer' ? metricValue : asset?.odometer ?? 0,
                metricType === 'hourmeter' ? metricValue : asset?.hourmeter ?? 0,
                asset?.chassis ?? "",
                asset?.group ?? "",
                asset?.modelCode ?? ""
            );
            setForm({
                ...form,
                checkup,
                asset: asset
                    ? {
                        ...asset,
                        [metricType]: metricValue,
                    }
                    : undefined,
            });
            setLoading(false);
        }, 500), [],
    );

    const setCheckupByGroupId = async (groupId: string) => {
        setLoading(true);
        const checkup = await getCheckupByMetric(
            form?.asset?.odometer ?? 0,
            form?.asset?.hourmeter ?? 0,
            form?.asset?.chassis ?? "",
            groupId ?? "",
            form?.asset?.modelCode ?? ""
        );
        setForm({
            ...form,
            checkup,
            asset: form?.asset
                ? {
                    ...form.asset,
                    group: groupId ?? "",
                }
                : undefined,
        });
        setLoading(false);
    }

    const checkIfUserIsDriver = (userProfile: any | null, drivers: Driver[]): boolean => {
        const driverId = userProfile?.sub?.split(":")[1];
        return drivers.some((driver) => driver.id === driverId);
    };

    const chooseDriver = (driverId: string, drivers: Driver[]) => {
        const driver = drivers.find((driver) => driver.id === driverId);
        setForm({ ...form, driver });
    };

    return {
        loading,
        vehicleLoading, setVehicleLoading,
        vehicleError, setVehicleError,
        searchAssetTerm, setSearchAssetTerm,
        setAssetId, assetId, chooseAsset, disableAssetGroup,
        filteredAssets,
        showComments, setShowComments,
        setCheckup,
        aditionalServices,
        setCheckupByGroupId,
        formIsValid,
        checkIfUserIsDriver, chooseDriver
    }
}

export function useSchedulingFormDealership(dealership?: Dealership, asset?: Asset, setAsset?: (asset: Asset) => void) {
    const [activeTab, setActiveTab] = useState<"favoritesTab" | "dealershipTab">("dealershipTab");
    const [showSearchConcessionaria, setShowSearchConcessionaria] = useState<boolean>(false);
    const [enableLocation, setEnableLocation] = useState<boolean>(false);

    const [favorites, setFavorites] = useState<Dealership[]>([]);
    const [dealerships, setDealerships] = useState<Dealership[]>([]);
    const [searchDealershipTerm, setSearchDealershipTerm] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const [assetGeoLocation, setAssetGeoLocation] = useState<AssetGeoLocation>();
    const [initialAssetAddress, setInitialAssetAddress] = useState<{ address: string, neighborhood: string, state: string, city: string, geoLocation: AssetGeoLocation }>();

    const [addressForm, setAddressForm] = useState<Record<'address' | 'neighborhood' | 'state' | 'city', string>>({
        address: dealership?.address ?? '',
        neighborhood: dealership?.neighborhood ?? '',
        state: dealership?.state ?? '',
        city: dealership?.city ?? '',
    });


    useEffect(() => {
        const fetchData = async () => {
            const favorites = await fetchFavoritesDealerships();
            setFavorites(favorites);
            
            if (asset?.geoLocation?.lat && asset?.geoLocation?.lng) {
                setAssetGeoLocation(asset?.geoLocation);
                
                const assetAddress = await getAddressByGeoLocation(asset?.geoLocation?.lat, asset?.geoLocation?.lng);
                setInitialAssetAddress({ 
                    address: assetAddress.address ?? '', 
                    neighborhood: assetAddress.neighborhood ?? '', 
                    state: assetAddress.state ?? '', 
                    city: assetAddress.city ?? '', 
                    geoLocation: asset?.geoLocation
                });

                updateAddress();
            } else {
                setEnableLocation(true);
            }
        }

        fetchData();


    }, []);

    const getFavoritesDealerships = async () => {
        const favorites = await fetchFavoritesDealerships();
        setFavorites(favorites);
    }

    const getDealershipsByGeolocation = async (liveLocation?: boolean) => {
        const dealerships = await fetchDealershipsByGeolocation(
            (liveLocation ? assetGeoLocation?.lat : asset?.geoLocation?.lat) || -23.550573750495325, 
            (liveLocation ? assetGeoLocation?.lng : asset?.geoLocation?.lng) || -46.64411772565403,
        );
        setDealerships(dealerships);
    }

    const getDealershipsByStateAndCity = async (city?: string, state?: string) => {
        const stateToUser = state ? states.find(s => s.name === state)?.state : (addressForm.state.includes('_') ? addressForm.state.split('_')[1] : addressForm.state);
        const dealerships = await fetchDealershipsByStateAndCity(stateToUser ?? '', city ?? addressForm.city);
        setDealerships(dealerships);
    }

    const updateGeoLocation = async (city: string) => {
        const geoLocation = await getGeoLocationByAddress(`${addressForm.address}, ${addressForm.neighborhood}, ${city}, ${addressForm.state.includes('_') ? addressForm.state.split('_')[1] : addressForm.state}`);
        if (asset?.id && setAsset) {
            setAsset({
                ...asset,
                geoLocation
            });
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await getDealershipsByGeolocation(true);
            if(asset?.id && setAsset) {
                setAsset({
                    ...asset,
                    geoLocation: assetGeoLocation
                });
            }
            setLoading(false);
        }

        fetchData();
    }, [assetGeoLocation]);

    const updateAddress = async (liveLocation?: boolean) => {
        const address = await getAddressByGeoLocation(
            liveLocation ? assetGeoLocation?.lat : asset?.geoLocation?.lat, 
            liveLocation ? assetGeoLocation?.lng : asset?.geoLocation?.lng
        );
        
        setAddressForm({
            address: address.address,
            neighborhood: address.neighborhood,
            city: address.city,
            state: address.state
        });

        if (liveLocation && asset?.id && setAsset) {
            setAsset({
                ...asset,
                geoLocation: assetGeoLocation,
            });
        }

        if (asset?.geoLocation?.lat && asset?.geoLocation?.lng) await getDealershipsByGeolocation();
        else if (liveLocation && assetGeoLocation?.lat && assetGeoLocation?.lng) await getDealershipsByGeolocation(true);
        else await getDealershipsByStateAndCity();
    }

    const handleSetEnableLocation = (manualLocationEnabled: boolean) => {
        setEnableLocation(manualLocationEnabled);


        if (manualLocationEnabled) [setAssetGeoLocation({ lat: -23.550573750495325, lng: -46.64411772565403 }), setAddressForm({ address: '', neighborhood: '', state: '', city: '' })]
        else [
            setAssetGeoLocation(initialAssetAddress?.geoLocation), 
            setAddressForm({ address: initialAssetAddress?.address ?? '', neighborhood: initialAssetAddress?.neighborhood ?? '', state: initialAssetAddress?.state ?? '', city: initialAssetAddress?.city ?? '' })
        ];
        setShowSearchConcessionaria(false);
    };

    useEffect(() => {
        const fetchData = async () => {
            const dealerships = await fetchDealerships(searchDealershipTerm);
            setDealerships(dealerships);
        }

        if(searchDealershipTerm?.length > 2) fetchData();
    }, [searchDealershipTerm]);

    return {
        assetGeoLocation, setAssetGeoLocation,
        activeTab, setActiveTab,
        showSearchConcessionaria, setShowSearchConcessionaria,
        enableLocation, handleSetEnableLocation,
        favorites, dealerships,
        searchDealershipTerm, setSearchDealershipTerm,
        loading, setLoading,
        addressForm, setAddressForm,
        updateGeoLocation, getFavoritesDealerships, getDealershipsByGeolocation, getDealershipsByStateAndCity,
    }
}

export interface IDateAndConsultantForm {
    scheduleDate?: Date;
    consultant?: DealershipConsultant;
}
export function useSchedulingFormDateAndConsultant(dealership?: Dealership, form?: IDateAndConsultantForm) {
    const [consultantloading, setConsultantLoading] = useState<boolean>(false);
    const [consultants, setConsultants] = useState<DealershipConsultant[]>([]);
    const [date, setDate] = useState<Date | undefined>();
    const [showDialog, setShowDialog] = useState(false);
    const [dialogContent, setDialogContent] = useState({
        title: "",
        body: ""
    });

    const [firstWeekDay, setFirstWeekDay] = useState<Date>(
        getFirstDayOfWeek(new Date())
    );
    const [weekDays, setWeekDays] = useState<any[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            setConsultantLoading(true);
            const consultants = await fetchConsultants(
                addDays(new Date(), 1),
                dealership?.id ?? ""
            );
            setConsultants(consultants);
            setDate(form?.scheduleDate ?? addDays(new Date(), 1));
            setConsultantLoading(false);
        };

        setWeekDays(
            getStringWeekDaysStartingFromDate(firstWeekDay).map((date, index) => ({
                date: addDays(firstWeekDay, index),
                weekDay: date.split(" ")[0],
                day: date.split(" ")[1],
            }))
        );

        fetchData();
    }, []);

    const previousWeek = () => {
        const lastDayOfPreviousWeek = addDays(firstWeekDay, -1);
        const firstWeekDayOfPreviousWeek = getFirstDayOfWeek(lastDayOfPreviousWeek);
        setWeekDays(
            getStringWeekDaysStartingFromDate(firstWeekDayOfPreviousWeek).map(
                (date, index) => ({
                    date: addDays(firstWeekDayOfPreviousWeek, index),
                    weekDay: date.split(" ")[0],
                    day: date.split(" ")[1],
                })
            )
        );
        setFirstWeekDay(firstWeekDayOfPreviousWeek);
    };

    const nextWeek = () => {
        const lastWeekDay = getLastDayOfWeek(firstWeekDay);
        const firstDayOfNextWeek = addDays(lastWeekDay, +1);
        setWeekDays(
            getStringWeekDaysStartingFromDate(firstDayOfNextWeek).map(
                (date, index) => ({
                    date: addDays(firstDayOfNextWeek, index),
                    weekDay: date.split(" ")[0],
                    day: date.split(" ")[1],
                })
            )
        );
        setFirstWeekDay(firstDayOfNextWeek);
    };

    const getAvailableHours = (selectedDate: Date) => {
        const dayOfWeek = selectedDate.getDay();
        if (dayOfWeek === 0) {
            return [];
        } else if (dayOfWeek === 6) {
            return Array.from({ length: 5 }, (_, i) => i + 8);
        } else {
            return Array.from({ length: 11 }, (_, i) => i + 8);
        }
    };

    const handleNextStep = (setStep: (step: number) => void) => {
        if (!form?.scheduleDate) {
            setDialogContent({
                title: "Atenção",
                body: consultants.length === 0 ? "Não existe consultor cadastrado para essa concessionária. Não é possível realizar o agendamento." : "É necessário escolher um consultor e uma data antes de prosseguir."
            });
            setShowDialog(true);
            return;
        }
        setStep(4);
    };

    return {
        consultantloading, setConsultantLoading,
        consultants, setConsultants,
        date, setDate,
        firstWeekDay, weekDays,
        previousWeek, nextWeek, getAvailableHours,
        showDialog, setShowDialog,
        dialogContent,
        handleNextStep
    }
}

export function useSchedulingFormSummary() {
    const userProfile = useSelector(getUserProfile);

    const [loading, setLoading] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [errorDialog, setErrorDialog] = useState({ show: false, title: '', body: '' });

    return {
        userProfile,
        loading, setLoading,
        acceptedTerms, setAcceptedTerms,
        successDialogOpen, setSuccessDialogOpen,
        errorDialog, setErrorDialog,
    }
}